import {
  Alert,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";

import { useCurrentOrg } from "@/providers/auth/hooks";
import { DiscoveredMcpTool, McpServerRecord } from "@/shared/domain/mcp";
import { useAdminCopyAllowedTools } from "@/shared/domain/mcp/hooks/useAdminCopyAllowedTools";
import { useAdminDiscoverTools } from "@/shared/domain/mcp/hooks/useAdminDiscoverTools";
import { useAdminUpdateAllowedTools } from "@/shared/domain/mcp/hooks/useAdminUpdateAllowedTools";
import { Button } from "@/shared/foundation/primitives";

import styles from "./McpServers.module.css";
import { OrgAutocomplete } from "./OrgAutocomplete";

interface EditAllowedToolsDialogProps {
  server: McpServerRecord | null;
  allServers: McpServerRecord[];
  onClose: () => void;
}

type Mode = "discover" | "copy" | "manual";

export function EditAllowedToolsDialog({
  server,
  allServers,
  onClose,
}: EditAllowedToolsDialogProps) {
  const currentOrgId = useCurrentOrg()?.org_id;
  const updateMutation = useAdminUpdateAllowedTools();
  const discoverMutation = useAdminDiscoverTools();
  const copyMutation = useAdminCopyAllowedTools();

  const [mode, setMode] = React.useState<Mode>("discover");
  const [toolStates, setToolStates] = React.useState<Record<string, boolean>>(
    {},
  );
  const [discoveredTools, setDiscoveredTools] = React.useState<
    DiscoveredMcpTool[]
  >([]);
  const [copySourceId, setCopySourceId] = React.useState("");
  const [manualInput, setManualInput] = React.useState("");
  const [discoverOrgId, setDiscoverOrgId] = React.useState("");

  // Start each opening from the server's saved allowlist.
  React.useEffect(() => {
    setToolStates({ ...server?.allowed_tools });
    setDiscoveredTools([]);
    setCopySourceId("");
    setManualInput("");
    setMode("discover");
    setDiscoverOrgId(server ? defaultDiscoverOrgId(server, currentOrgId) : "");
  }, [server, currentOrgId]);

  if (!server) {
    return null;
  }

  const handleDiscover = () => {
    if (!discoverOrgId) {
      return;
    }
    discoverMutation.mutate(
      { serverId: server.server_id, orgId: discoverOrgId },
      {
        onSuccess: (tools) => {
          setDiscoveredTools(tools);
          const newStates: Record<string, boolean> = {};
          for (const tool of tools) {
            // Preserve existing state if available, otherwise use readOnlyHint
            if (tool.name in toolStates) {
              newStates[tool.name] = toolStates[tool.name];
            } else {
              newStates[tool.name] = tool.read_only_hint !== false;
            }
          }
          setToolStates(newStates);
        },
      },
    );
  };

  const handleCopy = () => {
    if (!copySourceId) {
      return;
    }
    copyMutation.mutate(
      { serverId: server.server_id, sourceServerId: copySourceId },
      { onSuccess: () => onClose() },
    );
  };

  const handleAddManual = () => {
    const names = manualInput
      .split(/[,\n]/)
      .map((n) => n.trim())
      .filter(Boolean);
    const newStates = { ...toolStates };
    for (const name of names) {
      if (!(name in newStates)) {
        newStates[name] = true;
      }
    }
    setToolStates(newStates);
    setManualInput("");
  };

  const handleToggle = (name: string) => {
    setToolStates((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSave = () => {
    updateMutation.mutate(
      { serverId: server.server_id, allowedTools: toolStates },
      { onSuccess: () => onClose() },
    );
  };

  const handleClear = () => {
    updateMutation.mutate(
      { serverId: server.server_id, allowedTools: null },
      { onSuccess: () => onClose() },
    );
  };

  const enabledCount = Object.values(toolStates).filter(Boolean).length;
  const totalCount = Object.keys(toolStates).length;

  const copyableSources = allServers.filter(
    (s) => s.server_id !== server.server_id && s.allowed_tools !== null,
  );

  return (
    <Dialog open={server !== null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Allowed Tools &mdash; {server.display_name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Configure which tools from this server are available to users.
          {server.allowed_tools === null &&
            " Not yet configured — will auto-populate on first user discovery."}
        </Typography>

        {(updateMutation.isError ||
          discoverMutation.isError ||
          copyMutation.isError) && (
          <Alert severity="error" className={styles.alert}>
            {String(
              updateMutation.error ||
                discoverMutation.error ||
                copyMutation.error,
            )}
          </Alert>
        )}

        <div className={styles.toolModes}>
          <Button
            variant={mode === "discover" ? "contained" : "outlined"}
            size="small"
            onClick={() => setMode("discover")}
          >
            Discover
          </Button>
          <Button
            variant={mode === "copy" ? "contained" : "outlined"}
            size="small"
            onClick={() => setMode("copy")}
            disabled={copyableSources.length === 0}
          >
            Copy from Server
          </Button>
          <Button
            variant={mode === "manual" ? "contained" : "outlined"}
            size="small"
            onClick={() => setMode("manual")}
          >
            Manual Entry
          </Button>
        </div>

        {mode === "discover" && (
          <div className={styles.discoverSection}>
            <div className={styles.discoverRow}>
              <div className={styles.orgIdInput}>
                <OrgAutocomplete
                  selectedOrgIds={discoverOrgId ? [discoverOrgId] : []}
                  // One org at a time: a newly picked org replaces the previous one.
                  onChange={(orgIds) => setDiscoverOrgId(orgIds.at(-1) ?? "")}
                  helperText="Discovery uses your own connection to this server in this org, or else the org's token."
                />
              </div>
              <Button
                variant="contained"
                size="small"
                onClick={handleDiscover}
                disabled={discoverMutation.isPending || !discoverOrgId}
              >
                {discoverMutation.isPending
                  ? "Discovering..."
                  : "Discover Tools"}
              </Button>
            </div>
          </div>
        )}

        {mode === "copy" && (
          <div className={styles.copySection}>
            <Select
              size="small"
              value={copySourceId}
              onChange={(e) => setCopySourceId(e.target.value)}
              displayEmpty
              fullWidth
            >
              <MenuItem value="" disabled>
                Select a source server...
              </MenuItem>
              {copyableSources.map((s) => {
                const count = s.allowed_tools
                  ? Object.keys(s.allowed_tools).length
                  : 0;
                return (
                  <MenuItem key={s.server_id} value={s.server_id}>
                    {s.display_name} ({count} tools)
                  </MenuItem>
                );
              })}
            </Select>
            <Button
              variant="contained"
              size="small"
              onClick={handleCopy}
              disabled={!copySourceId || copyMutation.isPending}
            >
              {copyMutation.isPending ? "Copying..." : "Copy"}
            </Button>
          </div>
        )}

        {mode === "manual" && (
          <div className={styles.manualSection}>
            <TextField
              size="small"
              label="Tool names (comma or newline separated)"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleAddManual}
              disabled={!manualInput.trim()}
            >
              Add
            </Button>
          </div>
        )}

        {totalCount > 0 && (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              className={styles.toolCount}
            >
              {enabledCount}/{totalCount} tools enabled
            </Typography>
            <List dense className={styles.toolList}>
              {Object.entries(toolStates)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([name, enabled]) => {
                  const discovered = discoveredTools.find(
                    (t) => t.name === name,
                  );
                  return (
                    <ListItem key={name} className={styles.toolListItem}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={enabled}
                            onChange={() => handleToggle(name)}
                            size="small"
                          />
                        }
                        label={
                          <ListItemText
                            primary={name}
                            secondary={discovered?.description}
                          />
                        }
                      />
                      {discovered?.read_only_hint === true && (
                        <Chip label="read-only" size="small" color="success" />
                      )}
                      {discovered?.read_only_hint === false && (
                        <Chip label="read-write" size="small" color="warning" />
                      )}
                    </ListItem>
                  );
                })}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          color="warning"
          onClick={handleClear}
          disabled={updateMutation.isPending}
        >
          Clear (auto-populate)
        </Button>
        <div style={{ flex: 1 }} />
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={updateMutation.isPending || totalCount === 0}
        >
          {updateMutation.isPending ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The admin's current org when it can see the server, where they likely have
 * their own connection, so discovery doesn't run as another org's token; else
 * the server's first org.
 */
function defaultDiscoverOrgId(
  server: McpServerRecord,
  currentOrgId: string | undefined,
): string {
  const scopedOrgIds = server.org_ids;
  if (
    currentOrgId !== undefined &&
    (scopedOrgIds.length === 0 || scopedOrgIds.includes(currentOrgId))
  ) {
    return currentOrgId;
  }
  return scopedOrgIds[0] ?? "";
}
